import { useToken } from "providers/token";
import { useEffect, useMemo, useSyncExternalStore } from "react";

import type { OakStreamOptions, OakStreamResult } from "./types";

import { createStreamHash } from "./hash";
import { StreamObserver } from "./observer";
import { useOakStreamClient } from "./useOakStreamClient";

const NOOP_SUBSCRIBE = () => () => {};

export function useOakStream(options: OakStreamOptions): OakStreamResult {
  const client = useOakStreamClient();
  const { url, initialLastEventId, enabled = true } = options;
  const { getIdTokenRefreshIfExpired } = useToken();
  const streamHash = createStreamHash(options);

  const observer = useMemo(
    () => {
      if (!client) {
        return null;
      }
      return new StreamObserver(client, {
        enabled,
        url,
        initialLastEventId,
        getIdTokenRefreshIfExpired,
      });
    },
    // A new observer should only be created when the client identity or stream
    // identity changes. `enabled` is observer-local and is synced via the effect
    // below instead.
    // oxlint-disable-next-line exhaustive-deps
    [client, streamHash]
  );

  useEffect(
    function syncEnabledObserver() {
      if (!observer) return;
      void observer.setEnabled(enabled).catch(() => undefined);
    },
    [enabled, observer]
  );

  useEffect(
    function connectObserverOnMount() {
      if (!observer) return;
      void observer.connect().catch(() => undefined);

      return () => {
        observer.destroy();
      };
    },
    [observer]
  );

  const idleResult = useMemo<OakStreamResult>(
    () => ({
      status: "closed",
      data: [],
      error: null,
      lastEventId: initialLastEventId,
      restart: () => Promise.resolve(),
    }),
    [initialLastEventId]
  );

  return useSyncExternalStore(
    observer?.subscribe ?? NOOP_SUBSCRIBE,
    () => observer?.getCurrentResult() ?? idleResult,
    () => observer?.getCurrentResult() ?? idleResult
  );
}
